import { useState, useEffect, useRef } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Image, EnvelopeSimple, FloppyDisk, Eye, ArrowCounterClockwise, PaperPlaneTilt, Tag, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const VARIABLES = [
  { key: "client_name",      label: "Client name" },
  { key: "requester_name",   label: "Requester (contact) name" },
  { key: "title",            label: "Job/request title" },
  { key: "reference",        label: "Reference (e.g. JR-A1B2)" },
  { key: "property_address", label: "Property address" },
  { key: "acknowledger_name",label: "Acknowledger (admin) name" },
  { key: "company_name",     label: "Your company name" },
  { key: "date",             label: "Today's date" },
];

/**
 * Reusable acknowledgment email template editor.
 * - When `clientId` is empty, edits the tenant default.
 * - When `clientId` is provided, edits that client's override (or
 *   inherits from tenant default if no override exists yet).
 */
export default function AcknowledgmentEmailEditor({ clientId = "", clientLabel = "" }) {
  const [tpl, setTpl] = useState(null);
  const [tenantDefault, setTenantDefault] = useState(null);
  const [hasOverride, setHasOverride] = useState(false);
  const [level, setLevel] = useState("tenant_default");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [showTest, setShowTest] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);
  const bodyRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = clientId ? `?client_id=${encodeURIComponent(clientId)}` : "";
      const { data } = await API.get(`/email-templates/acknowledgment${params}`);
      setTpl(data.template);
      setTenantDefault(data.tenant_default || null);
      setHasOverride(!!data.has_override);
      setLevel(data.level);
    } catch {
      toast.error("Failed to load template");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [clientId]);

  const insertVariable = (key) => {
    const placeholder = `{${key}}`;
    if (bodyRef.current) {
      const el = bodyRef.current;
      const start = el.selectionStart || 0;
      const end = el.selectionEnd || 0;
      const next = (tpl.body_html || "").slice(0, start) + placeholder + (tpl.body_html || "").slice(end);
      setTpl({ ...tpl, body_html: next });
      setTimeout(() => {
        el.focus();
        el.selectionStart = el.selectionEnd = start + placeholder.length;
      }, 0);
    } else {
      setTpl({ ...tpl, body_html: (tpl.body_html || "") + placeholder });
    }
  };

  const handleSave = async () => {
    if (!tpl) return;
    setSaving(true);
    try {
      await API.put("/email-templates/acknowledgment", { ...tpl, client_id: clientId });
      toast.success(clientId ? "Client override saved" : "Tenant default saved");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const handleRevert = async () => {
    if (!clientId) return;
    if (!window.confirm("Remove this client override? Future requests for this client will use the tenant default.")) return;
    try {
      await API.delete(`/email-templates/acknowledgment/${clientId}`);
      toast.success("Override removed — falling back to tenant default");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove");
    }
  };

  const handlePreview = async () => {
    try {
      const { data } = await API.post("/email-templates/acknowledgment/preview", { template: tpl });
      setPreviewHtml(data.html);
      setPreviewSubject(data.subject);
      setShowPreview(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to render preview");
    }
  };

  const handleTestSend = async () => {
    if (!testEmail || !testEmail.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setTesting(true);
    try {
      await API.post("/email-templates/acknowledgment/test-send", { template: tpl, to_email: testEmail });
      toast.success(`Test sent to ${testEmail}`);
      setShowTest(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test send failed");
    } finally { setTesting(false); }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", "ack-email-logos");
    try {
      const { data } = await API.post("/upload", fd);
      const apiBase = process.env.REACT_APP_BACKEND_URL || "";
      const url = data.file_url?.startsWith("http") ? data.file_url : `${apiBase}${data.file_url}`;
      setTpl({ ...tpl, logo_url: url });
      toast.success("Logo uploaded");
    } catch {
      toast.error("Logo upload failed");
    }
  };

  if (loading || !tpl) return <p className="text-sm text-muted-foreground p-3">Loading template…</p>;

  return (
    <div className="space-y-4" data-testid="ack-email-editor">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-1.5">
            <EnvelopeSimple size={14} className="text-primary" />
            {clientId ? `Acknowledgment Email — ${clientLabel || "this client"}` : "Acknowledgment Email — Tenant Default"}
          </h3>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {clientId
              ? hasOverride
                ? "This client uses a custom acknowledgment email."
                : `This client inherits the tenant default. Save to create an override.`
              : "Sent automatically the moment a request or job is acknowledged. Used as fallback when a client has no override."}
            {level === "inherited_from_tenant" && " — Editing will create an override."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {clientId && hasOverride && (
            <Button variant="outline" size="sm" onClick={handleRevert} data-testid="ack-email-revert-btn">
              <Trash size={13} className="mr-1" /> Revert to default
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setShowTest(true)} data-testid="ack-email-test-btn">
            <PaperPlaneTilt size={13} className="mr-1" /> Send test
          </Button>
          <Button variant="outline" size="sm" onClick={handlePreview} data-testid="ack-email-preview-btn">
            <Eye size={13} className="mr-1" /> Preview
          </Button>
          <Button onClick={handleSave} disabled={saving} size="sm" data-testid="ack-email-save-btn">
            <FloppyDisk size={13} className="mr-1" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      {/* Enabled toggle */}
      <div className="flex items-center justify-between p-3 border border-border rounded-md bg-muted/30">
        <div>
          <p className="text-sm font-medium">Auto-send when a request/job is created</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            When ON, the email goes out instantly to the requester contact. CC list (below) receives a copy.
          </p>
        </div>
        <Switch
          checked={!!tpl.enabled}
          onCheckedChange={(v) => setTpl({ ...tpl, enabled: v })}
          data-testid="ack-email-enabled-toggle"
        />
      </div>

      {/* Sender details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Company name</Label>
          <Input
            value={tpl.company_name || ""}
            onChange={(e) => setTpl({ ...tpl, company_name: e.target.value })}
            placeholder="e.g. Smith Property Group"
            data-testid="ack-email-company-name"
          />
        </div>
        <div>
          <Label className="text-xs">From name (display)</Label>
          <Input
            value={tpl.from_name || ""}
            onChange={(e) => setTpl({ ...tpl, from_name: e.target.value })}
            placeholder="e.g. Smith Property Team"
            data-testid="ack-email-from-name"
          />
        </div>
        <div>
          <Label className="text-xs">From email address</Label>
          <Input
            type="email"
            value={tpl.from_email || ""}
            onChange={(e) => setTpl({ ...tpl, from_email: e.target.value })}
            placeholder="hello@smith-property.co.uk"
            data-testid="ack-email-from-email"
          />
          <p className="text-[10px] text-muted-foreground mt-1">
            ⚠ The domain must be verified with our email provider. If left blank, the platform's default sender is used.
          </p>
        </div>
        <div>
          <Label className="text-xs">Reply-to email</Label>
          <Input
            type="email"
            value={tpl.reply_to || ""}
            onChange={(e) => setTpl({ ...tpl, reply_to: e.target.value })}
            placeholder="replies@smith-property.co.uk"
            data-testid="ack-email-reply-to"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs">CC emails (comma separated)</Label>
          <Input
            value={tpl.cc_emails || ""}
            onChange={(e) => setTpl({ ...tpl, cc_emails: e.target.value })}
            placeholder="ops@yourco.com, alerts@yourco.com"
            data-testid="ack-email-cc"
          />
        </div>
      </div>

      {/* Logo upload */}
      <div className="border border-border rounded-md p-3">
        <Label className="text-xs flex items-center gap-1"><Image size={12} /> Logo (optional)</Label>
        <div className="flex items-center gap-3 mt-2">
          {tpl.logo_url ? (
            <img src={tpl.logo_url} alt="logo" className="h-12 max-w-[160px] object-contain rounded border border-border bg-white p-1" data-testid="ack-email-logo-preview" />
          ) : (
            <div className="h-12 w-32 border-2 border-dashed border-border rounded flex items-center justify-center text-[10px] text-muted-foreground">No logo</div>
          )}
          <input
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp"
            onChange={(e) => handleLogoUpload(e.target.files?.[0])}
            className="text-xs"
            data-testid="ack-email-logo-upload"
          />
          {tpl.logo_url && (
            <Button variant="outline" size="sm" onClick={() => setTpl({ ...tpl, logo_url: "" })} className="text-xs" data-testid="ack-email-logo-clear">
              <Trash size={12} className="mr-1" /> Remove
            </Button>
          )}
        </div>
      </div>

      {/* Subject */}
      <div>
        <Label className="text-xs">Subject line</Label>
        <Input
          value={tpl.subject || ""}
          onChange={(e) => setTpl({ ...tpl, subject: e.target.value })}
          placeholder="We're on it — {reference} {title}"
          data-testid="ack-email-subject"
        />
      </div>

      {/* Body */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <Label className="text-xs">Email body (HTML)</Label>
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Tag size={10} /> insert:</span>
            {VARIABLES.map(v => (
              <button
                key={v.key}
                type="button"
                onClick={() => insertVariable(v.key)}
                className="text-[10px] px-1.5 py-0.5 rounded bg-muted hover:bg-primary/15 text-foreground"
                title={v.label}
                data-testid={`ack-email-var-${v.key}`}
              >
                {`{${v.key}}`}
              </button>
            ))}
          </div>
        </div>
        <Textarea
          ref={bodyRef}
          value={tpl.body_html || ""}
          onChange={(e) => setTpl({ ...tpl, body_html: e.target.value })}
          rows={9}
          className="font-mono text-xs"
          data-testid="ack-email-body"
        />
      </div>

      {/* Signature */}
      <div>
        <Label className="text-xs">Signature</Label>
        <Textarea
          value={tpl.signature_html || ""}
          onChange={(e) => setTpl({ ...tpl, signature_html: e.target.value })}
          rows={3}
          className="font-mono text-xs"
          placeholder="<p>— The {company_name} team</p>"
          data-testid="ack-email-signature"
        />
      </div>

      {/* Preview dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="ack-email-preview-dialog">
          <DialogHeader>
            <DialogTitle>Preview</DialogTitle>
            <DialogDescription>How the email will look with sample data.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="text-xs"><strong>Subject:</strong> {previewSubject}</div>
            <div className="border border-border rounded-md overflow-hidden">
              <iframe srcDoc={previewHtml} title="preview" className="w-full" style={{ height: 460, border: "none" }} />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Test send dialog */}
      <Dialog open={showTest} onOpenChange={setShowTest}>
        <DialogContent data-testid="ack-email-test-dialog">
          <DialogHeader>
            <DialogTitle>Send test acknowledgment email</DialogTitle>
            <DialogDescription>Renders the template with sample placeholder data and sends to one address.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Send test to</Label>
            <Input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="ack-email-test-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTest(false)}>Cancel</Button>
            <Button onClick={handleTestSend} disabled={testing} data-testid="ack-email-test-send-btn">
              {testing ? "Sending…" : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {tenantDefault && clientId && hasOverride && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">View tenant default for reference</summary>
          <pre className="mt-2 p-3 border border-border rounded bg-muted/30 whitespace-pre-wrap text-[10px] max-h-48 overflow-auto">
{JSON.stringify({ subject: tenantDefault.subject, body: tenantDefault.body_html?.slice(0, 200) + "…" }, null, 2)}
          </pre>
        </details>
      )}

      <div className="flex items-start gap-2 mt-1 text-[10px] text-muted-foreground bg-muted/40 rounded-md p-2.5 border border-border/60">
        <ArrowCounterClockwise size={12} className="mt-0.5 shrink-0" />
        <span>
          The email goes to the request's <strong>requester contact email</strong>. If no requester contact is set,
          no email is sent. CC list receives a copy with subject prefixed <code>[CC]</code>.
        </span>
      </div>
    </div>
  );
}
